/**
 * modeGuard.js — Strict demo vs real mode enforcement
 *
 * Source of truth: localStorage 'ariaone-demo-mode' flag
 * (set during "Existing User" / Quick Demo login flow)
 *
 * Usage:
 *   import { isDemoMode, isRealMode, guardWrite } from '../lib/modeGuard';
 *
 *   // Check mode
 *   if (isDemoMode()) { ... }
 *
 *   // Guard a write action (blocks in demo, proceeds in real)
 *   guardWrite('Create appointment', () => { fetch(...) });
 *
 *   // In components with useAppMode hook:
 *   const { isDemo } = useAppMode();
 *   guardWrite('Save settings', saveFn, isDemo);
 */
import { toast } from 'sonner';

const DEMO_KEY = 'ariaone-demo-mode';

/**
 * Returns true if the app is in demo mode.
 * Pure function — reads localStorage directly. No React dependency.
 */
export const isDemoMode = () => {
  try {
    return localStorage.getItem(DEMO_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Returns true if the app is in real (authenticated) mode.
 */
export const isRealMode = () => !isDemoMode();

/**
 * Guard a write operation.
 *
 * In demo mode: blocks execution, shows a toast, returns null.
 * In real mode: executes the function and returns its result.
 *
 * @param {string} actionName — Human-readable name for the blocked action (shown in toast)
 * @param {Function} fn — The write function to execute (only called in real mode)
 * @param {boolean} [overrideIsDemo] — Optional explicit demo flag (from useAppMode hook). If omitted, reads localStorage.
 * @returns {*} Result of fn() in real mode, or null in demo mode.
 */
export const guardWrite = (actionName, fn, overrideIsDemo) => {
  const demo = overrideIsDemo !== undefined ? overrideIsDemo : isDemoMode();

  if (demo) {
    toast.info(`Demo mode — ${actionName}`, {
      description: 'This action is view-only in demo. Sign in with real credentials to make changes.',
    });
    return null;
  }

  return fn();
};

/**
 * Async version of guardWrite for async write operations.
 *
 * @param {string} actionName
 * @param {Function} asyncFn — Async function to execute
 * @param {boolean} [overrideIsDemo]
 * @returns {Promise<*>} Result of asyncFn() in real mode, or null in demo mode.
 */
export const guardWriteAsync = async (actionName, asyncFn, overrideIsDemo) => {
  const demo = overrideIsDemo !== undefined ? overrideIsDemo : isDemoMode();

  if (demo) {
    toast.info(`Demo mode — ${actionName}`, {
      description: 'This action is view-only in demo. Sign in with real credentials to make changes.',
    });
    return null;
  }

  return asyncFn();
};

/**
 * Returns demo fixture data if in demo mode, otherwise null.
 * Use for read paths that should show demo data only in demo mode.
 *
 * @param {*} fixtureData — The demo fixture to return
 * @param {boolean} [overrideIsDemo]
 * @returns {*} fixtureData in demo mode, null in real mode.
 */
export const demoFixture = (fixtureData, overrideIsDemo) => {
  const demo = overrideIsDemo !== undefined ? overrideIsDemo : isDemoMode();
  return demo ? fixtureData : null;
};

/**
 * Scope guard: require demo mode. Throws in real mode.
 * Use inside demo-only surfaces (e.g. LegacyEchoScheduler fixture loaders)
 * as an assertion that a code path is never reachable in real mode.
 *
 * @param {string} [scopeName='demo-only scope']
 */
export const requireDemoScope = (scopeName = 'demo-only scope') => {
  if (!isDemoMode()) {
    throw new Error(`requireDemoScope violated: ${scopeName} invoked while isReal=true`);
  }
};

/**
 * Scope guard: require real mode. Throws in demo mode.
 * Use inside real-only write paths (e.g. API mutation helpers) so an
 * accidental demo-mode call surfaces loudly in dev instead of silently
 * hitting the backend.
 *
 * @param {string} [scopeName='real-only scope']
 */
export const requireRealScope = (scopeName = 'real-only scope') => {
  if (isDemoMode()) {
    throw new Error(`requireRealScope violated: ${scopeName} invoked while isDemo=true`);
  }
};
